/**
 * Corrigir um registro do histórico (E137.F3, GUS-160, ADR-0093 §2.5): a correção entra como
 * registro novo, ligado a este, com o motivo; o registro original não muda.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';

import { Button, Field, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { corrigirEvento, HistoricoDoCasoError } from '@/lib/historico_do_caso';

const t = ptBR.historicoDoCaso.corrigir;
const MOTIVO_MAX = 500;

export interface CorrigirEventoProps {
  client: AxiosInstance;
  caseId: string;
  eventoId: string;
  onCorrigido: () => Promise<void>;
}

export function CorrigirEvento({
  client,
  caseId,
  eventoId,
  onCorrigido,
}: CorrigirEventoProps): JSX.Element {
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar(): Promise<void> {
    setSalvando(true);
    setErro(null);
    try {
      await corrigirEvento(client, caseId, eventoId, motivo.trim());
      setAberto(false);
      setMotivo('');
      await onCorrigido();
    } catch (err) {
      const semPermissao = err instanceof HistoricoDoCasoError && err.motivo === 'sem_permissao';
      setErro(semPermissao ? ptBR.historicoDoCaso.semPermissao : t.erro);
    } finally {
      setSalvando(false);
    }
  }

  if (!aberto) {
    return (
      <Button
        type="button"
        variant="ghost"
        data-testid={`historico-corrigir-${eventoId}`}
        onClick={() => setAberto(true)}
      >
        {t.abrir}
      </Button>
    );
  }
  return (
    <div className="mt-sc-2 space-y-sc-2" data-testid={`historico-correcao-${eventoId}`}>
      <Field label={t.motivo} description={t.explica}>
        <Textarea
          value={motivo}
          maxLength={MOTIVO_MAX}
          disabled={salvando}
          data-testid="historico-correcao-motivo"
          onChange={(evento) => setMotivo(evento.target.value)}
        />
      </Field>
      <div className="flex flex-wrap items-center gap-sc-3">
        <Button
          type="button"
          data-testid="historico-correcao-salvar"
          disabled={motivo.trim() === '' || salvando}
          onClick={() => void salvar()}
        >
          {salvando ? t.salvando : t.salvar}
        </Button>
        <Button type="button" variant="ghost" disabled={salvando} onClick={() => setAberto(false)}>
          {t.cancelar}
        </Button>
      </div>
      {erro !== null ? (
        <p role="alert" className="text-body-sm text-danger" data-testid="historico-correcao-erro">
          {erro}
        </p>
      ) : null}
    </div>
  );
}
